import type { BenefitNotice } from '../../../lib/site';

/**
 * 혜택 미리 계산 — **보여 주기 위한 값**이다.
 *
 * 진짜 금액은 결제 때 `payment_validation` 이 정한다. 여기서는 상세 화면이 "이것도 담으면
 * 얼마가 되는지" 를 즉시 보여 주려고 **엔진과 같은 규칙으로 흉내** 낸다(2026-08-24 사용자
 * 지시 "관련상품 체크하면 총 결제금액이 띠리링 돌아가면서 할인된 금액으로").
 *
 * 엔진(`payment_validation`)의 규칙을 그대로 옮겼다 —
 *  - 개수(`v_cart_cnt`)는 **담긴 대상 중 카트에 있는 상품 종류 수**다(수량이 아니다)
 *  - 구간(`s_bnfcnt ~ e_bnfcnt`)을 통과하면 `bnf_yn = true` 인 대상만 깎는다
 *  - 채널(`bnfprd`)이 지정되면 **그 단가로 산 줄만**. 교재는 채널과 무관하게 늘 깎인다
 *  - 정률은 `round(price - price * v / 100)`, 정액은 `price - v`
 *  - 기간연장은 금액이 아니라 기간이다(총액에 영향 없음)
 *
 * 서로 다른 혜택이 겹치면 엔진은 순서 없이 **둘 다** 적용한다(복리). 관리자 저장 단계에서
 * 대상 겹침을 막아 뒀으므로 여기서는 순서대로 더한다 — 남아 있는 이관분에서만 생길 일이다.
 */

/** 카트에 들어갈 한 줄 — 상세 화면이 지금 담으려는 것들 */
export interface CartLine {
  productId: number;
  /** 고른 단가(시청 가능 채널). 교재는 늘 `ALL` */
  platform: string;
  /** 그 단가의 판매가 */
  price: number;
  /** 교재 수량(강좌는 1) */
  quantity: number;
  isBook: boolean;
}

export interface Estimate {
  /** 깎이는 금액 합계 */
  discount: number;
  /**
   * **개수 조건**이 성립한 구성 — 더 담으라고 권할지 판단할 때 쓴다.
   * 개수는 **그 채널로 담긴 대상만** 센다(2026-08-26 교정 — `conditionMet` 주석 참고).
   */
  metKeys: string[];
  /**
   * **실제로 걸리는** 구성 — '적용 중' 배지가 이걸 본다.
   * 개수가 맞아도 **고른 단가가 혜택의 채널과 다르면 걸리지 않는다**(2026-08-24 사용자 지시).
   * 전체 단가에 걸린 혜택인데 PC·모바일 단가를 고르면 엔진이 그 줄을 건너뛴다.
   */
  activeKeys: string[];
}

export const noticeKey = (b: BenefitNotice) => `${b.id}-${b.num}`;

/**
 * 이 구성의 조건이 지금 카트로 성립하나 — **개수를 셀 때 채널도 본다**.
 *
 * 전에는 개수만 셌다("개수를 채우는 것과 어느 단가로 사는 것은 다른 문제" 라고 봤다).
 * 그랬더니 "모바일 강좌 2개 이상" 혜택에서 **메인을 PC 로 고르고 함께 담기에서 모바일
 * 하나를 고르면** 모바일은 하나뿐인데 조건이 성립해 할인이 붙었다(2026-08-25 제보).
 * 엔진도 같은 구멍이었고 함께 고쳤다(`payment_validation` — 마이그레이션
 * `20260826010000_benefit_count_respects_channel`).
 *
 * 교재는 단가가 하나뿐이라 채널을 따지지 않는다(엔진 `item_type = 'BOOK'` 과 같은 예외).
 */
export function conditionMet(b: BenefitNotice, lines: CartLine[]): boolean {
  const want = b.apply?.platform ?? null;
  const counted = want ? lines.filter((l) => l.isBook || l.platform === want) : lines;
  const ids = new Set(counted.map((l) => l.productId));
  const count = b.targets.filter((tg) => ids.has(tg.id)).length;
  if (!count) return false;
  if (b.countFrom != null && count < b.countFrom) return false;
  if (b.countTo != null && count > b.countTo) return false;
  return true;
}

/** 지금 카트로 얼마가 깎이나 */
export function estimate(benefits: BenefitNotice[], lines: CartLine[]): Estimate {
  const out: Estimate = { discount: 0, metKeys: [], activeKeys: [] };
  for (const b of benefits) {
    if (!conditionMet(b, lines)) continue;
    out.metKeys.push(noticeKey(b));

    const targets = new Set(b.targets.map((tg) => tg.id));
    const applied = new Set(b.targets.filter((tg) => tg.applied).map((tg) => tg.id));
    const ap = b.apply;
    const v = Number(ap?.value ?? 0);
    /**
     * 고른 단가가 이 혜택의 채널과 맞는 줄이 하나라도 있나 — 없으면 걸리지 않는다.
     * 판정 집합은 **대상 전체**다(할인이 꺼진 대상도 센다) — 엔진이 그렇게 본다.
     * 할인은 그중 적용을 켠 대상(`bnf_yn`)에만 걸린다.
     */
    let reached = false;
    for (const l of lines) {
      if (!targets.has(l.productId) || !ap) continue;
      // 채널 — 교재는 예외로 늘 깎인다(엔진 `item_type = 'BOOK'`)
      if (ap.platform && !l.isBook && l.platform !== ap.platform) continue;
      reached = true;
      if (!applied.has(l.productId)) continue;
      if (ap.type === 'RATE_DISCOUNT' && v > 0)
        out.discount += (l.price - Math.round(l.price - (l.price * v) / 100)) * l.quantity;
      else if (ap.type === 'AMOUNT_DISCOUNT' && v > 0)
        out.discount += Math.min(v, l.price) * l.quantity;
    }
    // 규칙이 없는 구성(증정만)은 개수만 맞으면 걸린다 — 채널을 볼 것이 없다
    if (!ap || reached) out.activeKeys.push(noticeKey(b));
  }
  return out;
}

/**
 * 관련 상품의 단가 한 줄 — 담길 때 **어느 단가로 담기나**(프론트 `priceLines` 와 같은 규칙).
 *
 * `want` 는 그 혜택이 걸린 채널이다 — **그 단가로 담아야 혜택이 걸린다**
 * (2026-08-24 사용자 제보: 모바일 단가 혜택인데 전체 단가로 담겨 할인이 안 됐다).
 * 그 채널로 팔지 않는 상품이면 파는 첫 줄로 떨어진다(그때는 이 혜택이 안 걸리는 게 맞다).
 */
export function targetLine(
  tg: BenefitNotice['targets'][number],
  priceDetail: boolean,
  want?: string | null,
): { platform: string; price: number } {
  const by: Record<string, number> = { ALL: tg.price, PC: tg.pricePc, MOBILE: tg.priceMobile };
  const sold = tg.salePlatforms.length ? tg.salePlatforms : ['ALL'];
  const lines = priceDetail ? sold : sold.slice(0, 1);
  const platform = want && lines.includes(want) ? want : lines[0] ?? 'ALL';
  return { platform, price: by[platform] ?? tg.price };
}

/**
 * 함께 담으라고 권할 대상 — **이 상품 하나로는 조건이 안 되는 구성**의 다른 대상.
 *
 * `exclude` 는 이미 그 화면에 체크박스로 있는 것(관련 교재)이다 — 그 자리에 말풍선을
 * 붙이므로 목록에서는 뺀다(2026-08-24 사용자 지시). 같은 상품이 여러 구성에 있어도 한 번만.
 */
export type PromotedTarget = BenefitNotice['targets'][number] & {
  /** 이 대상을 권한 혜택의 채널 — **그 단가로 담아야 걸린다**(없으면 어느 단가든 걸린다) */
  wantPlatform: string | null;
};

export function promotedTargets(
  benefits: BenefitNotice[],
  baseMetKeys: string[],
  exclude?: Set<number>,
): PromotedTarget[] {
  const base = new Set(baseMetKeys);
  const out = new Map<number, PromotedTarget>();
  for (const b of benefits) {
    if (base.has(noticeKey(b))) continue;
    for (const tg of b.targets) {
      if (tg.self || out.has(tg.id) || exclude?.has(tg.id)) continue;
      // 같은 상품을 두 혜택이 권하면 먼저 만난 쪽의 채널을 쓴다(둘 다 만족시킬 수는 없다)
      out.set(tg.id, { ...tg, wantPlatform: b.apply?.platform ?? null });
    }
  }
  return [...out.values()];
}
